/* eslint-disable */
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Card,
  CardContent,
  Chip,
  Grid,
  Divider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useSelector, useDispatch } from "react-redux";
import {
  addBulkRooms,
  addNewRoom,
  listAllRooms,
  resetAllRoomsList,
  listAllUserGames,
  resetUserGames, // Add this import
} from "../../store/actions";
import * as yup from "yup";
import { Formik } from "formik";
import {
  Add,
  Close,
  People,
  CalendarToday,
  Schedule,
  Email,
} from "@mui/icons-material";
import * as XLSX from "xlsx";

const Rooms = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const smScreen = useMediaQuery(theme.breakpoints.up("sm"));
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState("single");
  const [excelFile, setExcelFile] = useState(null);
  const [sendEmail, setSendEmail] = useState(false); // New state for email checkbox

  const [gamesModalOpen, setGamesModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState("");

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setUploadMode("single");
    setExcelFile(null);
    setSendEmail(false); // Reset email checkbox state
  };

  const handleGamesModalOpen = (userId, userName) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setGamesModalOpen(true);
    dispatch(listAllUserGames(userId));
  };

  const handleGamesModalClose = () => {
    setGamesModalOpen(false);
    setSelectedUserId(null);
    setSelectedUserName("");
    dispatch(resetUserGames());
  };

  const {
    isLoading,
    roomsList,
    showViewMore,
    userGamesList,
    userGamesLoading,
  } = useSelector(
    ({
      loading,
      admin: {
        rooms: { list, showViewMore },
        userGames: { list: gamesList = [], loading: gamesLoading = false } = {},
      },
    }) => ({
      isLoading: loading,
      roomsList: list,
      showViewMore,
      userGamesList: gamesList,
      userGamesLoading: gamesLoading,
    })
  );

  useEffect(() => {
    dispatch(resetAllRoomsList());
    dispatch(resetUserGames());
    fetchMoreData();
  }, [dispatch]);

  const fetchMoreData = () => {
    dispatch(listAllRooms());
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: smScreen ? 400 : "50%",
    bgcolor: colors.primary[400],
    outline: "none",
    boxShadow: 24,
    borderRadius: "8px",
    border: `1px solid ${colors.grey[600]}`,
    p: 2,
  };

  const gamesModalStyle = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: smScreen ? "85vw" : "95vw",
    maxWidth: "800px",
    height: "85vh",
    maxHeight: "800px",
    bgcolor: colors.primary[400],
    outline: "none",
    boxShadow: `0 20px 60px rgba(0,0,0,0.8)`,
    borderRadius: "16px",
    border: `2px solid ${colors.blueAccent[500]}`,
    overflow: "hidden",
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
  };

  const handleFormSubmit = async (values) => {
    try {
      await dispatch(addNewRoom(values));
      handleClose();
    } catch (error) {
      alert("Failed to add user");
    }
  };

  const handleBulkSubmit = async () => {
    if (!excelFile) return alert("Please upload an Excel file");

    const reader = new FileReader();
    reader.onload = async (e) => {
      const data = new Uint8Array(e.target?.result);
      const workbook = XLSX.read(data, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (!jsonData.length) {
        alert("Excel file is empty or invalid");
        return;
      }

      try {
        // Include the sendEmail flag in the bulk upload payload
        const bulkUploadData = {
          users: jsonData, // array of users
          sendEmail: sendEmail, // whether to send email to users
        };
        
        await dispatch(addBulkRooms(bulkUploadData));
        handleClose();
      } catch (error) {
        console.log(error);
      }
    };

    reader.readAsArrayBuffer(excelFile);
  };

  const initialValues = {
    email: "",
    name: "",
  };

  const checkoutSchema = yup.object().shape({
    email: yup.string().email("Invalid email").required("Required"),
  });

  const columns = [
    { field: "_id", headerName: "User Id", width: 200 },
    {
      field: "username",
      headerName: "Username",
      width: 150,
      cellClassName: "name-column--cell",
    },
    { field: "email", headerName: "Email", width: 250 },
    {
      field: "is_active",
      headerName: "View Games",
      width: 130,
      renderCell: ({ row }) => {
        return (
          <Button
            variant="contained"
            size="small"
            onClick={() => handleGamesModalOpen(row._id, row.username)}
            sx={{
              backgroundColor: colors.greenAccent[600],
              color: colors.grey[100],
              "&:hover": {
                backgroundColor: colors.greenAccent[700],
              },
            }}
          >
            View Games
          </Button>
        );
      },
    },
  ];

  // Function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Function to get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return colors.greenAccent[600];
      case "running":
        return colors.blueAccent[600];
      case "pending":
        return colors.redAccent[600];
      default:
        return colors.grey[600];
    }
  };

  // Game Card Component
  const GameCard = ({ game }) => {
    const viewGameUrl = `https://mybingo.social/games/${game.organizer_name}?game_id=${game._id}`;

    return (
      <Card
        sx={{
          backgroundColor: colors.primary[400],
          borderRadius: "12px",
          border: `1px solid ${colors.grey[700]}`,
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: `0 8px 25px rgba(0,0,0,0.3)`,
            border: `1px solid ${colors.blueAccent[500]}`,
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography
              variant="h6"
              sx={{
                color: colors.grey[100],
                fontWeight: "bold",
                fontSize: "1.1rem",
              }}
            >
              Game #{game._id?.slice(-8)}
            </Typography>
            <Chip
              label={game.status || "Unknown"}
              sx={{
                backgroundColor: getStatusColor(game.status),
                color: colors.grey[100],
                fontWeight: "bold",
                textTransform: "capitalize",
              }}
            />
          </Box>

          <Divider sx={{ borderColor: colors.grey[600], mb: 2 }} />

          {/* Game Details */}
          <Stack spacing={1.5}>
            {/* Participants */}
            <Box display="flex" alignItems="center" gap={1}>
              <People
                sx={{ color: colors.blueAccent[400], fontSize: "20px" }}
              />
              <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                <strong>Participants:</strong> {game.users?.length || 0} players
              </Typography>
            </Box>

            {/* Created Date */}
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarToday
                sx={{ color: colors.greenAccent[400], fontSize: "18px" }}
              />
              <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                <strong>Created:</strong> {formatDate(game.createdAt)}
              </Typography>
            </Box>

            {/* Organizer */}
            <Box>
              <Typography
                variant="body2"
                sx={{ color: colors.grey[400], fontSize: "0.85rem" }}
              >
                <strong>Organizer:</strong> {game.organizer_name}
              </Typography>
            </Box>
          </Stack>

          {/* Footer */}
          <Box
            mt={3}
            pt={2}
            borderTop={`1px solid ${colors.grey[700]}`}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Typography variant="caption" sx={{ color: colors.grey[500] }}>
              Game ID: {game._id}
            </Typography>

            <Box display="flex" gap={1}>
              {game.status === "running" && (
                <Chip
                  label="Live"
                  size="small"
                  sx={{
                    backgroundColor: colors.redAccent[600],
                    color: colors.grey[100],
                    animation: "pulse 2s infinite",
                    "@keyframes pulse": {
                      "0%": { opacity: 1 },
                      "50%": { opacity: 0.7 },
                      "100%": { opacity: 1 },
                    },
                  }}
                />
              )}
              <Button
                size="small"
                variant="contained"
                href={viewGameUrl}
                target="_blank"
                sx={{
                  backgroundColor: colors.blueAccent[500],
                  color: colors.grey[100],
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "0.75rem",
                  "&:hover": {
                    backgroundColor: colors.blueAccent[600],
                  },
                }}
              >
                View Game
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box m="20px">
      <Box
        display={smScreen ? "flex" : "block"}
        flexDirection={smScreen ? "row" : "column"}
        justifyContent={smScreen ? "space-between" : "start"}
        alignItems={smScreen ? "center" : "start"}
        m="20px 0"
      >
        <Header title="USERS" subtitle="Welcome to Users" />
        <Box>
          <Button
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
            }}
            onClick={handleOpen}
          >
            <Add sx={{ mr: "10px" }} />
            Add New User
          </Button>
        </Box>
      </Box>

      <Box
        m="8px 0 0 0"
        height="80vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .name-column--cell": { color: colors.greenAccent[300] },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid
          rows={roomsList}
          columns={columns}
          getRowId={(row) => row?._id}
          loading={isLoading}
        />
      </Box>

      {/* Add New User Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <Header title="ADD NEW USER" titleVariant="h4" />

          {/* Toggle Buttons */}
          <Stack direction="row" spacing={2} mb={2}>
            <Button
              variant={uploadMode === "single" ? "contained" : "outlined"}
              onClick={() => setUploadMode("single")}
              fullWidth
            >
              Single User
            </Button>
            <Button
              variant={uploadMode === "bulk" ? "contained" : "outlined"}
              onClick={() => setUploadMode("bulk")}
              fullWidth
            >
              Bulk Upload
            </Button>
          </Stack>

          {uploadMode === "bulk" ? (
            <Stack spacing={2}>
              <Button variant="contained" component="label">
                Upload Excel File
                <input
                  type="file"
                  hidden
                  accept=".xlsx, .xls"
                  onChange={(e) =>
                    setExcelFile(e.target?.files ? e.target.files[0] : null)
                  }
                />
              </Button>

              {excelFile && (
                <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                  📄 {excelFile.name}
                </Typography>
              )}

              {/* Email Notification Checkbox */}
              <Box
                sx={{
                  p: 2,
                  backgroundColor: colors.primary[500],
                  borderRadius: "8px",
                  border: `1px solid ${colors.grey[600]}`,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      sx={{
                        color: colors.blueAccent[400],
                        "&.Mui-checked": {
                          color: colors.blueAccent[600],
                        },
                      }}
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Email 
                        sx={{ 
                          color: sendEmail ? colors.blueAccent[400] : colors.grey[400],
                          fontSize: "20px" 
                        }} 
                      />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: sendEmail ? colors.grey[100] : colors.grey[400],
                          fontWeight: sendEmail ? "bold" : "normal"
                        }}
                      >
                        Send welcome email to all users
                      </Typography>
                    </Box>
                  }
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      flex: 1,
                    },
                  }}
                />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: colors.grey[500],
                    display: "block",
                    mt: 1,
                    ml: 4
                  }}
                >
                  {sendEmail 
                    ? "Users will receive an email notification after account creation"
                    : "Users will be created without email notification"
                  }
                </Typography>
              </Box>

              <Button
                fullWidth
                size="large"
                color="secondary"
                variant="contained"
                onClick={handleBulkSubmit}
                disabled={isLoading}
                sx={{
                  fontSize: "15px",
                  py: 1.5,
                }}
              >
                {isLoading ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress color="inherit" size={20} />
                    <span>Uploading Users...</span>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Add />
                    <b>
                      Upload Users 
                      {sendEmail && " & Send Emails"}
                    </b>
                  </Box>
                )}
              </Button>
            </Stack>
          ) : (
            <Formik
              onSubmit={handleFormSubmit}
              initialValues={initialValues}
              validationSchema={checkoutSchema}
            >
              {({
                values,
                errors,
                touched,
                handleBlur,
                handleChange,
                handleSubmit,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="filled"
                      type="email"
                      label="Email of the user"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.email}
                      name="email"
                      error={!!touched.email && !!errors.email}
                      helperText={touched.email && errors.email}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      variant="filled"
                      type="text"
                      label="Name (optional)"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.name}
                      name="name"
                      error={!!touched.name && !!errors.name}
                      helperText={touched.name && errors.name}
                    />
                    <Button
                      fullWidth
                      size="large"
                      type="submit"
                      color="secondary"
                      variant="contains"
                      sx={{ fontSize: "15px" }}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <CircularProgress color="inherit" size={24} />
                      ) : (
                        <b>Submit</b>
                      )}
                    </Button>
                  </Stack>
                </form>
              )}
            </Formik>
          )}
        </Box>
      </Modal>

      {/* User Games Modal with Cards - UPDATED */}
      <Modal
        open={gamesModalOpen}
        onClose={handleGamesModalClose}
        sx={{
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            zIndex: 9998,
          },
        }}
      >
        <Box sx={gamesModalStyle}>
          {/* Modal Header - Fixed */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 3,
              borderBottom: `2px solid ${colors.blueAccent[500]}`,
              backgroundColor: colors.primary[500],
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  color: colors.grey[100],
                  fontWeight: "bold",
                  mb: 0.5,
                  fontSize: { xs: "1.5rem", sm: "2rem" },
                }}
              >
                🎮 GAMES FOR {selectedUserName?.toUpperCase()}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  color: colors.blueAccent[300],
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                All participated games for user: {selectedUserName}
              </Typography>
            </Box>
            <IconButton
              onClick={handleGamesModalClose}
              sx={{
                color: colors.grey[100],
                backgroundColor: colors.redAccent[600],
                width: 48,
                height: 48,
                "&:hover": {
                  backgroundColor: colors.redAccent[700],
                  transform: "scale(1.1)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <Close fontSize="large" />
            </IconButton>
          </Box>

          {/* Main Content Area - Scrollable */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 3,
              backgroundColor: colors.primary[400],
              "&::-webkit-scrollbar": {
                width: "12px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: colors.primary[500],
                borderRadius: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: colors.blueAccent[600],
                borderRadius: "6px",
                "&:hover": {
                  backgroundColor: colors.blueAccent[500],
                },
              },
            }}
          >
            {/* Loading State */}
            {userGamesLoading && (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                height="400px"
              >
                <CircularProgress
                  size={60}
                  sx={{
                    color: colors.blueAccent[400],
                    mb: 3,
                  }}
                />
                <Typography variant="h5" color={colors.grey[100]} mb={1}>
                  Loading Games...
                </Typography>
                <Typography variant="body1" color={colors.grey[400]}>
                  Please wait while we fetch the game data
                </Typography>
              </Box>
            )}

            {/* Games Grid */}
            {!userGamesLoading && userGamesList && userGamesList.length > 0 && (
              <Grid container spacing={3}>
                {userGamesList.map((game, index) => (
                  <Grid item xs={12} sm={6} key={game._id || index}>
                    <GameCard game={game} />
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Empty State */}
            {!userGamesLoading &&
              (!userGamesList || userGamesList.length === 0) && (
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  height="400px"
                  sx={{
                    backgroundColor: colors.primary[500],
                    borderRadius: "16px",
                    border: `3px dashed ${colors.blueAccent[400]}`,
                    mx: 2,
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: "4rem",
                      mb: 2,
                    }}
                  >
                    🎮
                  </Typography>
                  <Typography
                    variant="h4"
                    color={colors.grey[100]}
                    mb={2}
                    textAlign="center"
                  >
                    No Games Found
                  </Typography>
                  <Typography
                    variant="h6"
                    color={colors.grey[400]}
                    textAlign="center"
                    maxWidth="400px"
                  >
                    This user hasn't participated in any games yet. Games will
                    appear here once they join their first game.
                  </Typography>
                </Box>
              )}
          </Box>

          {/* Summary Footer - Fixed at bottom */}
          {!userGamesLoading && userGamesList && userGamesList.length > 0 && (
            <Box
              sx={{
                p: 3,
                backgroundColor: colors.primary[500],
                borderTop: `2px solid ${colors.blueAccent[500]}`,
                borderBottomLeftRadius: "16px",
                borderBottomRightRadius: "16px",
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 4,
                  flexWrap: "wrap",
                }}
              >
                <Box textAlign="center">
                  <Typography
                    variant="h4"
                    color={colors.blueAccent[400]}
                    fontWeight="bold"
                  >
                    {userGamesList.length}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Total Games
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography
                    variant="h4"
                    color={colors.greenAccent[400]}
                    fontWeight="bold"
                  >
                    {
                      userGamesList.filter((g) => g.status === "completed")
                        .length
                    }
                  </Typography>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Completed
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography
                    variant="h4"
                    color={colors.redAccent[400]}
                    fontWeight="bold"
                  >
                    {userGamesList.filter((g) => g.status === "running").length}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Running
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default Rooms;